import React, { useEffect, useState, useContext } from "react";
import Sidebar from "../../components/layout/Sidebar";
import Navbar from "../../components/layout/Navbar"; // Añadimos el Navbar para coherencia
import { getJobs } from "../../services/jobs.service";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { 
  Plus, 
  Search, 
  Calendar, 
  Hash, 
  PlayCircle, 
  FilterX, 
  FileText,
  Clock
} from "lucide-react";

export default function Jobs() {
  const { user } = useContext(AuthContext);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Filtros
  const [jobId, setJobId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    const loadInitialJobs = async () => {
      if (!user) return;
      setLoading(true);
      const token = localStorage.getItem("token") || "";
      const data = await getJobs(token, null, null, null);
      setJobs(data);
      setLoading(false);
    };
    loadInitialJobs();
  }, [user]);

  const fetchJobs = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    const token = localStorage.getItem("token") || "";
    const data = await getJobs(
      token,
      jobId ? null : startDate || null,
      jobId ? null : endDate || null,
      jobId || null
    );
    if (data.length === 0) setError("No se encontraron resultados.");
    setJobs(data);
    setLoading(false);
  };

  const clearFilters = () => {
    setJobId("");
    setStartDate("");
    setEndDate("");
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />

      <div className="flex-1 flex flex-col">
        <Navbar />

        <main className="p-6 md:p-10 space-y-8">
          {/* HEADER ACCIÓN */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="text-3xl font-black text-slate-900 tracking-tight">Lista de Jobs</h2>
              <p className="text-slate-500 text-sm">Gestiona y monitorea las órdenes de trabajo en tiempo real.</p>
            </div>

            <button
              onClick={() => navigate("/jobs/create")}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg shadow-blue-600/20 active:scale-95 text-sm"
            >
              <Plus className="w-5 h-5" /> Agregar Nuevo Job
            </button>
          </div>

          {/* BARRA DE FILTROS ESTILIZADA */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center gap-2 text-slate-400 mb-2">
              <Search className="w-4 h-4" />
              <span className="text-xs font-bold uppercase tracking-wider">Filtros de búsqueda</span>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Job ID */}
              <div className="relative">
                <Hash className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <input
                  type="number"
                  value={jobId}
                  placeholder="ID del Job"
                  onChange={(e) => setJobId(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                />
              </div>

              {/* Fecha Inicio */}
              <div className="relative">
                <Calendar className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <input
                  type="date"
                  value={startDate}
                  disabled={!!jobId}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-xl outline-none text-sm transition-all ${
                    jobId ? "bg-slate-100 text-slate-400 border-slate-200" : "bg-slate-50 border-slate-200 focus:ring-2 focus:ring-blue-500"
                  }`}
                />
              </div>

              {/* Fecha Fin */}
              <div className="relative">
                <Calendar className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <input
                  type="date"
                  value={endDate}
                  disabled={!!jobId}
                  onChange={(e) => setEndDate(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-xl outline-none text-sm transition-all ${
                    jobId ? "bg-slate-100 text-slate-400 border-slate-200" : "bg-slate-50 border-slate-200 focus:ring-2 focus:ring-blue-500"
                  }`}
                />
              </div>

              {/* Botones de Filtro */}
              <div className="flex gap-2">
                <button
                  onClick={fetchJobs}
                  disabled={loading}
                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl text-sm transition-all disabled:opacity-50"
                >
                  {loading ? "..." : "Filtrar"}
                </button>
                <button
                  onClick={clearFilters}
                  className="p-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl transition-all"
                  title="Limpiar filtros"
                >
                  <FilterX className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* TABLA DE RESULTADOS */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {loading ? (
              <div className="p-20 text-center space-y-4">
                <div className="animate-spin w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-slate-500 font-medium">Sincronizando base de datos...</p>
              </div>
            ) : error ? (
              <div className="p-20 text-center">
                <div className="bg-red-50 text-red-500 p-4 rounded-xl inline-block mb-4">
                  <FilterX className="w-8 h-8" />
                </div>
                <p className="text-slate-600 font-bold">{error}</p>
                <button onClick={clearFilters} className="text-blue-500 text-sm font-bold mt-2 underline">Ver todos los trabajos</button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="py-4 px-6 text-xs font-black text-slate-400 uppercase tracking-widest">ID / Código</th>
                      <th className="py-4 px-6 text-xs font-black text-slate-400 uppercase tracking-widest">Proceso</th>
                      <th className="py-4 px-6 text-xs font-black text-slate-400 uppercase tracking-widest text-center">Tiempos (Inicio / Fin)</th>
                      <th className="py-4 px-6 text-xs font-black text-slate-400 uppercase tracking-widest">Observación</th>
                      <th className="py-4 px-6 text-xs font-black text-slate-400 uppercase tracking-widest text-right">Acción</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {jobs.map((job) => (
                      <tr key={job.jobId} className="hover:bg-blue-50/30 transition-colors group">
                        <td className="py-5 px-6">
                          <div className="flex flex-col">
                            <span className="text-sm font-black text-slate-900">#{job.jobId}</span>
                            <span className="text-[11px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md self-start mt-1 uppercase">
                              {job.processCode}
                            </span>
                          </div>
                        </td>
                        <td className="py-5 px-6">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-slate-100 rounded-lg group-hover:bg-white transition-colors">
                              <FileText className="w-4 h-4 text-slate-400" />
                            </div>
                            <span className="text-sm font-bold text-slate-700">{job.processName}</span>
                          </div>
                        </td>
                        <td className="py-5 px-6">
                          <div className="flex flex-col items-center gap-1">
                            <div className="flex items-center gap-2 text-[11px] text-slate-500 font-medium">
                              <Clock className="w-3 h-3 text-emerald-500" />
                              {job.startDate ? new Date(job.startDate).toLocaleDateString() : "—"}
                            </div>
                            <div className="flex items-center gap-2 text-[11px] text-slate-400 font-medium italic">
                              Fin: {job.endDate ? new Date(job.endDate).toLocaleDateString() : "No definido"}
                            </div>
                          </div>
                        </td>
                        <td className="py-5 px-6 max-w-xs">
                          <p className="text-xs text-slate-500 truncate italic">
                            {job.observation || "Sin observaciones registradas."}
                          </p>
                        </td>
                        <td className="py-5 px-6 text-right">
                          <button
                            onClick={() => navigate(`/jobs/${job.jobId}/execute`)}
                            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-blue-600 text-white px-4 py-2 rounded-xl text-xs font-black transition-all group-hover:scale-105"
                          >
                            <PlayCircle className="w-4 h-4" /> Ejecutar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}